import type { Submission, DraftRecording } from '@/types'
import type { TaxonomyPayload } from '@/types/taxonomy'
import { newSubmissionId } from './ids'
import { isOtherTaxonomyId } from './taxonomySelection'

// PHI/PII keywords to block (common identifiers)
const PHI_KEYWORDS = [
  'ssn',
  'social security',
  'credit card',
  'passport',
  'driver license',
  'medical record',
  'patient id',
  'date of birth',
  'dob',
  'phone number',
  'email address',
  '@gmail.com',
  '@yahoo.com',
  '@outlook.com',
]

/**
 * Checks if text contains potential PHI/PII keywords
 */
export function containsPHI(text: string): boolean {
  const lowerText = text.toLowerCase()
  return PHI_KEYWORDS.some((keyword) => lowerText.includes(keyword.toLowerCase()))
}

/**
 * Validates submission data structure
 */
export interface ValidationResult {
  valid: boolean
  errors: string[]
}

/**
 * Creates a new submission from note text
 */
export function createSubmissionFromNote(
  noteText: string,
  assetId: string,
  indicationId: string,
  kiqIds: string[],
  userId: string | null = null
): Submission {
  const now = new Date().toISOString()
  return {
    id: newSubmissionId(),
    type: 'text',
    userId,
    assetId,
    indicationId,
    kiqIds,
    status: 'saved',
    createdAt: now,
    updatedAt: now,
    noteBody: noteText,
    textPreview: noteText.substring(0, 200),
    syncedAt: null,
  }
}

export function validateSubmission(
  submission: Partial<Submission>,
  taxonomy: TaxonomyPayload | null,
): ValidationResult {
  const errors: string[] = []

  const assetById = (id: string) => taxonomy?.assets.find((asset) => asset.id === id)
  const indicationById = (id: string) =>
    taxonomy?.indications.find((indication) => indication.id === id)
  const kiqById = (id: string) =>
    taxonomy?.keyInsightQuestions.find((kiq) => kiq.id === id)

  // Check required fields
  if (!submission.assetId) {
    errors.push('Asset selection is required')
  } else if (!isOtherTaxonomyId(submission.assetId) && !assetById(submission.assetId)) {
    errors.push('Selected asset is not valid')
  }

  if (!submission.indicationId) {
    errors.push('Indication selection is required')
  } else if (
    !isOtherTaxonomyId(submission.indicationId) &&
    !indicationById(submission.indicationId)
  ) {
    errors.push('Selected indication is not valid')
  }

  if (!submission.kiqIds || submission.kiqIds.length === 0) {
    errors.push('At least one Key Insight Question must be selected')
  } else if (submission.kiqIds.length === 1 && isOtherTaxonomyId(submission.kiqIds[0])) {
    // Fallback taxonomy is valid even though it is not part of the fetched taxonomy payload.
  } else {
    // Validate all KIQs exist and belong to the indication
    const invalidKiqs = submission.kiqIds.filter(
      (kiqId) => !kiqById(kiqId) || kiqById(kiqId)?.indicationId !== submission.indicationId,
    )
    if (invalidKiqs.length > 0) {
      errors.push('One or more selected questions are not valid for this indication')
    }
  }

  // Type-specific validation
  if (submission.type === 'text') {
    if (!submission.noteBody || submission.noteBody.trim().length === 0) {
      errors.push('Note text is required for text submissions')
    } else if (submission.noteBody.length > 1000) {
      errors.push('Note text cannot exceed 1000 characters')
    } else if (containsPHI(submission.noteBody)) {
      errors.push(
        'Note contains potentially sensitive information. Please remove personal identifiers.'
      )
    }
  } else if (submission.type === 'audio') {
    if (!submission.audioBlobKey && submission.status === 'saved') {
      errors.push('Audio recording is required for audio submissions')
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * Validates draft recording data
 */
export function validateDraftRecording(draft: Partial<DraftRecording>): ValidationResult {
  const errors: string[] = []

  if (!draft.assetId) {
    errors.push('Asset selection is required')
  }
  if (!draft.indicationId) {
    errors.push('Indication selection is required')
  }
  if (!draft.kiqIds || draft.kiqIds.length === 0) {
    errors.push('At least one Key Insight Question must be selected')
  }
  if (!draft.blob) {
    errors.push('Audio recording is required')
  }
  if (!draft.durationMs || draft.durationMs <= 0) {
    errors.push('Recording duration must be greater than zero')
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * Validates note text input
 */
export function validateNoteText(text: string): ValidationResult {
  const errors: string[] = []

  if (text.length > 1000) {
    errors.push(`Note cannot exceed 1000 characters (current: ${text.length})`)
  }

  if (containsPHI(text)) {
    errors.push(
      'Note contains potentially sensitive information. Please remove personal identifiers.'
    )
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}
